// @ts-check
import AppConfig from '../appConfig';
import BaseController from './BaseController';

import MetadataDataSource from '../dataSources/MetadataDataSource';
import ProjectDataSource from '../dataSources/ProjectDataSource';
import MapDataSource from '../dataSources/MapDataSource';
import CustomMapSelectionDataSource from '../dataSources/CustomMapSelectionDataSource';
import SearchDataSource from '../dataSources/SearchDataSource';

import { buildExportFileName, exportTargetLabel } from '../helpers/exportFileName';
import { downloadServerExport, triggerBlobDownload } from '../helpers/serverExport';
import {
    serializeAppliedFiltersForExport,
    serializeCombinerForExport,
} from '../helpers/pointsFiltersForExport';
import LocationAnalysisItemOrigin from '../enums/LocationAnalysisItemOrigin';
import PointsDataSource from '../dataSources/PointsDataSource';
import VisualReportDataSource from '../dataSources/VisualReportDataSource';
import {
    isEqualJson,
    mapLocationAnalysisItemToVisualReport,
    polygonParam,
} from '../helpers/Util';
import { SELFSTORAGE_POINT_TYPE } from '../enums/SelfStoragePointType';
import { MIN_VERTICES } from '../helpers/customShape';

/** @type {Array.<import('../').Config.FeaturedReport>} */
const FEATURED_REPORTS = AppConfig.constants.featuredReports;

/**
 * @class
 * @extends BaseController
 */
class CustomMapSelectionController extends BaseController {
    // @ts-ignore
    static get name() {
        return 'CustomMapSelectionController';
    }

    static getInstance(options) {
        return new CustomMapSelectionController(options);
    }

    onActivate() {
        this.bindGluBusEvents({
            ENTER_UPDATE_LOCATION_ANALYSIS_MODE: this.onEnterUpdateLocationAnalysisMode,
            EXIT_LOCATION_ANALYSIS_MODE: this.onExitLocationAnalysisMode,
            LOCATION_ANALYSIS_ITEMS_REQUEST: this.onLocationAnalysisItemsRequest,
            SET_LOCATION_ANALYSIS_SELECTION: this.onSetLocationAnalysisSelection,
            SET_LOCATION_ANALYSIS_TYPE_AND_SELECTION: this.onSetLocationAnalysisTypeAndSelection,
            SET_ACTIVE_LOCATION_ANALYSIS_ITEM_DEAL_ID: this.setActiveLocationAnalysisItemDealId,
            UPDATE_ACTIVE_LOCATION_ANALYSIS_ITEM_DEAL_DATA:
                this.updateActiveLocationAnalysisItemDealData,
            CUSTOM_MAP_SELECTION_CONTOUR_REQUEST: this.onCustomMapSelectionContourRequest,
            CUSTOM_MAP_SELECTION_FEATURED_REPORTS_REQUEST: this.onFeaturedReportsRequest,
            CUSTOM_LOCATION_ANALYSIS_VALUES_REQUEST: this.onCustomLocationAnalysisValuesRequest,
            SAVE_CUSTOM_LOCATION_ANALYSIS_VALUE_REQUEST: this.onSaveCustomLocationAnalysisValue,
            CLOSE_LOCATION_ANALYSIS_REQUEST: this.onCloseLocationAnalysis,
            DOWNLOAD_SERVER_EXPORT_REQUEST: this.onDownloadServerExport,
            APPLY_POINTS_FILTERS_REQUEST: this.onApplyPointsFiltersSnapshot,
            CLEAR_FACILITY_FILTER: this.onClearPointsFiltersSnapshot,
            CLEAR_FACILITY_FILTER_FROM_PANEL: this.onClearPointsFiltersSnapshot,
            POLYGON_DRAW_RESPONSE: this.onPolygonDrawn,
            CUSTOM_SHAPE_CLEAR_REQUEST: this.onClearCustomShape,
        });

        /** @type {import('../').AppliedPointFilters} */
        this.appliedFilters = {};
        /** @type {string | null} */
        this.filterCombinerType = null;

        /** @type {import('../dataSources/MetadataDataSource').default} */
        this.metadataDataSource = this.activateSource(MetadataDataSource);
        /** @type {import('../dataSources/MapDataSource').default} */
        this.mapDataSource = this.activateSource(MapDataSource);
        /** @type {import('../dataSources/ProjectDataSource').default} */
        this.projectDataSource = this.activateSource(ProjectDataSource);
        /** @type {import('../dataSources/CustomMapSelectionDataSource').default} */
        this.customMapSelectionDataSource = this.activateSource(CustomMapSelectionDataSource);
        /** @type {import('../dataSources/SearchDataSource').default} */
        this.searchDataSource = this.activateSource(SearchDataSource);
        this.pointsDataSource = this.activateSource(PointsDataSource);
        /** @type {import('../dataSources/VisualReportDataSource').default} */
        this.visualReportDataSource = this.activateSource(VisualReportDataSource);
    }

    /**
     * @param {import('../objects/LocationAnalysisItem').default} item
     */
    async _hydrateSavedDeal(item) {
        const facilityId = item.id;
        const isFacility =
            item.type === SELFSTORAGE_POINT_TYPE.SS_FACILITY ||
            item.type === SELFSTORAGE_POINT_TYPE.SS_CONSTRUCTION;
        if (item.dealId || !facilityId || !isFacility) return;

        let deals = this.visualReportDataSource.visualReports;
        if (!deals.length) {
            try {
                deals = await this.visualReportDataSource.loadVisualReports();
            } catch (error) {
                console.error(error);
                return;
            }
        }

        const forFacility = deals.filter(deal => deal.facilityId === facilityId);
        if (!forFacility.length) return;

        const selection = Array.from(item.selection).sort();
        const profile = item.analysisType ? item.analysisType.VISUAL_REPORT_PROFILE : undefined;
        const exact = forFacility.find(
            deal =>
                deal.profile === profile &&
                deal.selection &&
                isEqualJson([...deal.selection].sort(), selection),
        );
        const deal = exact || forFacility[0];

        item.dealId = deal.id;
        if (deal.listId != null) item.listId = deal.listId;
        if (deal.status != null) item.status = deal.status;
        if (deal.notes != null) item.notes = deal.notes;
        if (deal.dealListName != null) item.dealListName = deal.dealListName;
    }

    /**
     * @param {object} param0
     * @param {string} param0.mapInstanceId
     */
    onCloseLocationAnalysis({ mapInstanceId }) {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);

        this.bus.emit('EXIT_LOCATION_ANALYSIS_MODE', {
            mapInstanceId: mapInstance.id,
        });
    }

    /**
     * A confirmed custom shape. It reshapes the open analysis, which keeps its
     * own point as the centre; the point need not lie inside the shape.
     * @param {{mapInstanceId: string, ring: [number, number][]}} payload
     */
    onPolygonDrawn = ({ mapInstanceId, ring }) => {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
        const item = mapInstance.locationAnalysisItem;
        if (!item) return;
        // What Clear custom shape goes back to.
        if (item.analysisTypeId !== 'POLYGON' && item.selection && item.selection.size) {
            item.previousAnalysis = {
                analysisTypeId: item.analysisTypeId,
                selection: new Set(item.selection),
            };
        }
        item.ring = ring;
        this.setActiveLocationAnalysisItemType(mapInstanceId, 'POLYGON', new Set([0]));
        this.bus.emit('GET_STORAGE_FACILITY_COUNT_REQUEST', { mapInstanceId });
    };

    /**
     * Drops the shape, from the deal too, and applies the radius or drive time given.
     * @param {{mapInstanceId: string, analysisTypeId: string, selection: Set<number>}} payload
     */
    onClearCustomShape = ({ mapInstanceId, analysisTypeId, selection }) => {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
        if (!mapInstance.locationAnalysisItem) return;
        // null, not undefined: the update then sends a blank polygon, which the server drops.
        mapInstance.locationAnalysisItem.ring = null;
        this.onSetLocationAnalysisTypeAndSelection({ mapInstanceId, analysisTypeId, selection });
        this.bus.emit('GET_STORAGE_FACILITY_COUNT_REQUEST', { mapInstanceId });
    };

    onApplyPointsFiltersSnapshot = ({ filterCombinerType, appliedFilters }) => {
        this.appliedFilters = appliedFilters || {};
        this.filterCombinerType = filterCombinerType || null;
    };

    onClearPointsFiltersSnapshot = () => {
        this.appliedFilters = {};
        this.filterCombinerType = null;
    };

    /**
     * Handle server-side export. Rails handles limit enforcement, XLSX generation,
     * and returns the binary file.
     */
    async onDownloadServerExport({ mapInstanceId, report, reportId }) {
        try {
            const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
            const { locationAnalysisItem } = mapInstance;
            const { analysisType } = locationAnalysisItem;

            const selections = locationAnalysisItem.sortedSelectionAsArray || [];
            const profileValues = selections.join(',');
            const profile = analysisType.VISUAL_REPORT_PROFILE;
            const isFacility = locationAnalysisItem.type === SELFSTORAGE_POINT_TYPE.SS_FACILITY;

            /** @type {Record<string, any>} */
            const params = {
                lat: locationAnalysisItem.point.lat,
                lng: locationAnalysisItem.point.lng,
                profile,
                'profile-values': profileValues,
                address: isFacility ? '' : locationAnalysisItem.value || '',
                facility_name: isFacility ? locationAnalysisItem.value || '' : '',
                deal_id: locationAnalysisItem.dealId,
            };
            if (locationAnalysisItem.isPolygon) {
                params.polygon = polygonParam(locationAnalysisItem.ring);
            }

            /** @type {string} */
            let endpoint;
            /** @type {string} */
            let fileName;

            switch (report.source) {
                case 'survey':
                    endpoint = '/exports/commercial-developments';
                    fileName = report.name;
                    break;
                case 'olap':
                    ({ endpoint, fileName } = this._resolveOlapExport(report));
                    break;
                default:
                    throw new Error(`Server export not supported for source: ${report.source}`);
            }

            fileName = buildExportFileName(
                fileName,
                exportTargetLabel(locationAnalysisItem.value, params.address, {
                    lat: locationAnalysisItem.point.lat,
                    lng: locationAnalysisItem.point.lng,
                }),
            );

            params.file_name = fileName;

            if (report.id === 'selfstorage_facility_data') {
                const filters = serializeAppliedFiltersForExport(this.appliedFilters);
                if (Object.keys(filters).length > 0) {
                    params.filters = filters;
                    params.filter_combiner = serializeCombinerForExport(this.filterCombinerType);
                }
            }

            const result = await downloadServerExport(endpoint, params);
            if (!result || result.error) {
                const errorMessage = result.message ? result.message : 'Export failed';
                this.bus.emit('DOWNLOAD_SERVER_EXPORT_ERROR', { reportId, errorMessage });
                return;
            }

            triggerBlobDownload(result, fileName);
            this.bus.emit('REPORT_LIMITS_LOAD_REQUEST');
            this.bus.emit('DOWNLOAD_SERVER_EXPORT_SUCCESS', {
                reportId,
                reportName: report.name,
                lat: locationAnalysisItem.point.lat,
                lng: locationAnalysisItem.point.lng,
            });
        } catch (error) {
            console.error('Server export failed:', error);
            this.bus.emit('DOWNLOAD_SERVER_EXPORT_ERROR', {
                reportId,
                errorMessage: 'Export failed unexpectedly. Please try again.',
            });
        }
    }

    /** @private */
    _resolveOlapExport(report) {
        const OLAP_EXPORT_MAP = {
            olap_demography_report: {
                endpoint: '/exports/demographic',
                fileName: 'Demography',
            },
            olap_sqft_per_capita_report: {
                endpoint: '/exports/sqft-per-capita',
                fileName: 'Square Foot per Capita',
            },
            selfstorage_facility_data: {
                endpoint: '/exports/storage-facilities',
                fileName: 'Storage Facilities',
            },
            olap_housing_starts: {
                endpoint: '/exports/housing-developments',
                fileName: 'Housing Developments',
            },
        };

        const mapping = OLAP_EXPORT_MAP[report.id];
        if (!mapping) {
            throw new Error(`Unknown OLAP report ID for server export: ${report.id}`);
        }
        return mapping;
    }

    /**
     * @param {object} param0
     * @param {import('../objects/LocationAnalysisItem').default} param0.selectedItem
     * @param {string} param0.mapInstanceId
     * @param {boolean} param0.showInsights
     */
    async onEnterUpdateLocationAnalysisMode({
        selectedItem,
        mapInstanceId,
        showInsights = false,
        visualReportProps = {},
    }) {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);

        // Get reverse geocode address for custom pins
        if (
            AppConfig.constants.useHereReverseGeocode &&
            selectedItem.itemOrigin === LocationAnalysisItemOrigin.CUSTOM_PIN
        ) {
            /** @type {GeocodeResultAddress | null} */
            let address = null;
            try {
                address = await this.searchDataSource.hereReverseGeocode({
                    point: selectedItem.point,
                });
            } catch (error) {
                console.error(error);
            }
            selectedItem.value = address ? address.label.toString() : selectedItem.value;
        }

        /**
         * Check if analysis type is null, if it is
         * null map should zoom in to our pin
         */
        if (!selectedItem._analysisTypeId) {
            const objectToEmit = {
                mapInstanceId,
                center: selectedItem.point,
                zoom: 15,
            };
            this.bus.emit('MAP_SET_CENTER_REQUEST', objectToEmit);
        }

        const isNewAnalysis = !mapInstance.isLocationAnalysisActive;
        const isItemChanged =
            !isNewAnalysis &&
            (!mapInstance.locationAnalysisItem.equalCenter(selectedItem) ||
                mapInstance.locationAnalysisItem.itemOrigin !== selectedItem.itemOrigin);

        mapInstance.locationAnalysisItem = selectedItem;

        if (isNewAnalysis) {
            this.bus.emit('SET_MIN_SUMMARY_LEVEL', mapInstanceId);
            this.bus.emit('LOCATION_ANALYSIS_MODE_STARTED', { mapInstanceId });
        } else if (isItemChanged) {
            this.bus.emit('CLEAR_CUSTOM_MAP_SELECTION', { mapInstanceId });
        }
        this.bus.emit('SET_LOCATION_ANALYSIS_SELECTION', {
            mapInstanceId,
            selection: selectedItem.selection,
        });

        this.bus.emit('SHOW_SEARCHED_ITEM_POPUP', { forceOpenFacilityPopup: false });
        if (showInsights && mapInstance.locationAnalysisItem) {
            await this._hydrateSavedDeal(mapInstance.locationAnalysisItem);
            const {
                value,
                point,
                selection,
                analysisType,
                dealId,
                type,
                listId,
                status,
                notes,
                dealListName,
            } = mapInstance.locationAnalysisItem;

            const visualReport = {
                id: dealId,
                title: value,
                selection: Array.from(selection),
                profile: analysisType.VISUAL_REPORT_PROFILE,
                report: AppConfig.constants.defaultInsightsReport,
                point,
                pointType: type,
                dealListName: dealListName,
                status: status,
                listId: listId,
                notes: notes,
                polygon: mapInstance.locationAnalysisItem.isPolygon
                    ? polygonParam(mapInstance.locationAnalysisItem.ring)
                    : undefined,
                ...visualReportProps,
            };

            this.bus.emit('SHOW_INSIGHTS', visualReport);
            this.bus.emit('GET_STORAGE_FACILITY_COUNT_REQUEST', {
                mapInstanceId: mapInstanceId,
            });
        }

        // In order to reconcile visual components upon selection update
        // we need to trigger refresh event but only after the current
        // event cycle, therefore we use setTimeout with no real timeout
        setTimeout(() => {
            this.bus.emit('LOCATION_ANALYSIS_ITEMS_REQUEST');
        }, 0);
    }

    /**
     * @param {object} param0
     * @param {string} param0.mapInstanceId
     */
    onExitLocationAnalysisMode({ mapInstanceId }) {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
        mapInstance.locationAnalysisItem = undefined;

        this.bus.emit('LOCATION_ANALYSIS_MODE_EXITED', { mapInstanceId });

        this.bus.emit('TOGGLE_INSIGHT_PANEL', { newValue: false });
    }

    onLocationAnalysisItemsRequest = () => {
        // Since this feature is only available for one map instance, we will use the default
        const mapInstance = this.projectDataSource.defaultMapInstance;
        this.bus.emit('LOCATION_ANALYSIS_ITEMS', mapInstance.locationAnalysisItems);
    };

    setActiveLocationAnalysisItemDealId = ({ mapInstanceId, dealId }) => {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
        if (!mapInstance.locationAnalysisItem) return;
        mapInstance.locationAnalysisItem.dealId = dealId;
    };

    /**
     * Update all deal-related fields on the active location analysis item.
     * Used when a deal is saved/updated from the insights iframe.
     * @param {object} param0
     * @param {string} param0.mapInstanceId
     * @param {object} param0.dealData
     */
    updateActiveLocationAnalysisItemDealData = ({ mapInstanceId, dealData }) => {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
        if (!mapInstance.locationAnalysisItem || !dealData) return;

        const item = mapInstance.locationAnalysisItem;
        if (dealData.title != null) item.value = dealData.title;
        if (dealData.id != null) item.dealId = dealData.id;
        if (dealData.status != null) item.status = dealData.status;
        if (dealData.notes != null) item.notes = dealData.notes;
        if (dealData.listId != null) item.listId = dealData.listId;

        this.bus.emit('LOCATION_ANALYSIS_ITEM_UPDATED', {
            mapInstanceId,
            locationAnalysisItem: item,
        });
    };

    /**
     * Set the analysis type for the active location analysis item
     * @param {string} mapInstanceId
     * @param {string} analysisTypeId
     * @param {Set<number>} selection
     */
    setActiveLocationAnalysisItemType = (mapInstanceId, analysisTypeId, selection) => {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);

        if (!mapInstance.locationAnalysisItem) return;

        if (analysisTypeId === undefined) {
            mapInstance.locationAnalysisItem.selection = new Set();
        }

        const previousAnalysisTypeId = mapInstance.locationAnalysisItem.analysisTypeId;
        mapInstance.locationAnalysisItem.analysisTypeId = analysisTypeId;
        // The ring survives a switch to a radius or drive time, so the area
        // selector can switch straight back to it; only POLYGON sends it.

        if (previousAnalysisTypeId !== mapInstance.locationAnalysisItem.analysisTypeId) {
            // Update active location analysis item selection
            const activeSelection = selection.size
                ? selection
                : new Set([mapInstance.locationAnalysisItem.analysisType.VALUES_ARRAY[0]]);
            this.setActiveLocationAnalysisItemSelection(mapInstanceId, activeSelection);
        } else {
            this.setActiveLocationAnalysisItemSelection(mapInstanceId, selection);
        }

        const visualReport = mapLocationAnalysisItemToVisualReport(
            mapInstance.locationAnalysisItem,
        );

        // A shape is only persisted once it has been drawn (a reshape lands here too).
        // The deal keeps its ring whatever the profile, so it is sent on every switch.
        const { ring } = mapInstance.locationAnalysisItem;
        if (visualReport && visualReport.id && mapInstance.locationAnalysisItem.isComplete) {
            this.bus.emit('VISUAL_REPORT_UPDATE_REQUEST', {
                reportId: visualReport.id,
                selection: visualReport.selection,
                profile: visualReport.profile,
                polygon:
                    ring && ring.length >= MIN_VERTICES ? polygonParam(ring) : ring === null ? '' : undefined,
            });
        }

        this.bus.emit('LOCATION_ANALYSIS_ITEM_UPDATED', {
            mapInstanceId,
            locationAnalysisItem: mapInstance.locationAnalysisItem,
        });
    };

    /**
     * Set selection for the active location analysis item
     * @param {string} mapInstanceId
     * @param {Set<number>} selection
     */
    setActiveLocationAnalysisItemSelection = (mapInstanceId, selection) => {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);

        if (!mapInstance.locationAnalysisItem) return;

        switch (mapInstance.locationAnalysisItem.analysisTypeId) {
            case 'RADIUS':
            case 'DRIVING_TIME':
                this.bus.emit('CUSTOM_MAP_SELECTION_CONTOUR_REQUEST', {
                    mapInstanceId: mapInstance.id,
                    selection,
                });
                break;
            // The shape is already drawn; nothing to fetch.
            case 'POLYGON':
                this.bus.emit('CUSTOM_MAP_SELECTION_SOURCE_DATA', {
                    isIsochrone: true,
                    feature: mapInstance.locationAnalysisItem.selectionFeatures,
                    labels: mapInstance.locationAnalysisItem.polygonLabelFeatures,
                    mapInstanceId,
                });
                break;
            default:
            // do nothing
        }

        // In case of "empty" location analysis (location without selection parameters
        // like drive times or radius), we should still re-center the map to point to
        // the location coordinates. This situation happens when user first perform
        // geo-search, opening the location panel without starting the analysis and
        // than pick any saved location. In that moment, we have a point but no selection
        if (selection.size === 0 && mapInstance.locationAnalysisItem.point) {
            this.bus.emit('MAP_SET_CENTER_REQUEST', {
                mapInstanceId,
                center: mapInstance.locationAnalysisItem.point,
            });
        }

        mapInstance.locationAnalysisItem.selection = selection;

        this.bus.emit('LOCATION_ANALYSIS_ITEM_UPDATED', {
            mapInstanceId,
            locationAnalysisItem: mapInstance.locationAnalysisItem,
        });
        this.bus.emit('VISUAL_REPORTS_GET_REQUEST', { source: this });
    };

    /**
     * Set analysis type and selection for the active location analysis item and location analysis
     * items in the compare list
     * @param {object} payload
     * @param {string} payload.mapInstanceId
     * @param {string} payload.analysisTypeId
     * @param {Set<number>} payload.selection
     */
    onSetLocationAnalysisTypeAndSelection({ mapInstanceId, analysisTypeId, selection }) {
        this.setActiveLocationAnalysisItemType(mapInstanceId, analysisTypeId, selection);
        this.onSetLocationAnalysisSelection({ mapInstanceId, selection });

        // The default rings for the next pin are a radius or a drive time, never a shape.
        if (analysisTypeId === 'POLYGON') return;
        this.bus.emit('USER_PREFERENCES_UPDATE_REQUEST', {
            analysis_type: analysisTypeId === 'DRIVING_TIME' ? 'driving' : 'radius',
            selection: Array.from(selection),
        });
    }

    /**
     * Set selection for the active location analysis item and location analysis
     * items in the compare list
     * @param {object} payload
     * @param {string} payload.mapInstanceId
     * @param {Set<number>} payload.selection
     */
    onSetLocationAnalysisSelection = ({ mapInstanceId, selection }) => {
        this.setActiveLocationAnalysisItemSelection(mapInstanceId, selection);
    };

    onCustomLocationAnalysisValuesRequest() {
        const { customLocationAnalysisValues } = this.metadataDataSource;
        this.bus.emit('CUSTOM_LOCATION_ANALYSIS_VALUES', {
            customLocationAnalysisValues,
        });
    }

    /** @param {{value: number; analysisTypeUnit: import('../').LocationAnalysisTypeUnit}} payload */
    onSaveCustomLocationAnalysisValue({ value, analysisTypeUnit }) {
        this.metadataDataSource.addCustomLocationAnalysisValue(value, analysisTypeUnit);
    }

    /**
     * Used to create a list on feature report tab
     */
    onFeaturedReportsRequest() {
        // If there are no featured reports just return an empty array
        if (FEATURED_REPORTS.length === 0) {
            this.bus.emit('CUSTOM_MAP_SELECTION_FEATURED_REPORTS', []);
            return;
        }

        // we need to find surveyNames in featured reports (if any), so we can retrieve their metadata and assemble the
        // featuredReports response
        const surveyNames = FEATURED_REPORTS.filter(
            featuredReport => featuredReport.source === 'survey',
        ).map(featuredSurveyReport => featuredSurveyReport.properties.surveyName);

        if (surveyNames.length === 0) {
            this.onFeaturedReportsMetaReady();
        } else {
            this.metadataDataSource
                .loadPremadeSurveyReports(surveyNames)
                .then(reportsForSurveys => this.onFeaturedReportsMetaReady(reportsForSurveys));
        }
    }

    onFeaturedReportsMetaReady(reportsForSurveys) {
        /** @type {Array.<import('../types').FeaturedSurveyReport | import('../types').FeaturedOlapReport>} */
        const featuredReports = FEATURED_REPORTS.flatMap(featuredReport => {
            switch (featuredReport.source) {
                case 'olap': {
                    return {
                        source: featuredReport.source,
                        id: featuredReport.properties.id,
                        name: featuredReport.properties.name,
                        disabledForStarterTier: featuredReport.disabledForStarterTier === true,
                    };
                }
                case 'survey': {
                    const reportNamesToShow = featuredReport.properties.reportNamesToShow;
                    // surveyReports are provided in-order as they are specified in featuredReports (where source=survey)
                    return reportsForSurveys
                        .shift()
                        .filter(surveyReport => {
                            // discard unwanted report ids
                            if (reportNamesToShow) {
                                return reportNamesToShow.includes(surveyReport.name);
                            }
                            return true;
                        })
                        .map(surveyReport => ({
                            source: featuredReport.source,
                            name: surveyReport.name,
                            // TODO: Find out the best way to implement this
                            // this is survey code of the survey that will be used to get the min summary level
                            // in order to switch the map to that view. For PacComm use case
                            // this was selected to be PACCOMDEM2019, because we used it's block
                            // groups summary level. Opportunities reports are an exception.
                            // In ideal implementation that should be selected either by the user
                            // or read from the current map instance
                            surveyCode: featuredReport.properties.surveyName,
                            reportTopicId: surveyReport.id.toString(),
                            aggregationType: featuredReport.properties.aggregationType,
                            downloadReport: featuredReport.properties.downloadReport,
                            disabledForStarterTier: featuredReport.disabledForStarterTier === true,
                        }));
                }
                default:
                    // should not happen
                    throw new Error(
                        `Loading featured report of unknown source '${featuredReport.source}'`,
                    );
            }
        });

        this.bus.emit('CUSTOM_MAP_SELECTION_FEATURED_REPORTS', featuredReports);
    }

    /**
     * CONTOUR SELECTION: radiuses and isochrones
     * @param {import('../').LocationAnalysisContourRequest} payload
     */
    onCustomMapSelectionContourRequest({ mapInstanceId, selection }) {
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
        const { locationAnalysisItem } = mapInstance;

        this.customMapSelectionDataSource
            .getContourFeatures(
                locationAnalysisItem.point,
                locationAnalysisItem.contourType,
                Array.from(selection),
                true,
            )
            .then(locationContourData => {
                locationAnalysisItem.selection = selection;
                locationAnalysisItem.sourceData = locationContourData;
                this.bus.emit('CUSTOM_MAP_SELECTION_SOURCE_DATA', {
                    isIsochrone: locationAnalysisItem.isIsochrone,
                    feature: locationAnalysisItem.selectionFeatures,
                    labels: locationAnalysisItem.polygonLabelFeatures,
                    mapInstanceId,
                });
            });
    }

    onDeactivate() {
        this.unbindGluBusEvents();
    }
}

export default CustomMapSelectionController;

/**
 * @typedef SourceLayerDataset
 * @property {string|number} datasetId
 * @property {string[]} columns
 *
 * @typedef SourceLayer
 * @property {string} layerId
 * @property {SourceLayerDataset[]} datasets
 *
 *
 */
